import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

/** Available animation effects for the loader */
export type LoaderAnimation =
  | 'spin'
  | 'pulse'
  | 'bounce'
  | 'fade'
  | 'shake'
  | 'spiral'
  | 'flash'
  | 'jello'
  | 'rubberband'
  | 'tada'
  | 'flip3d'
  | 'meteor'
  | 'breath'
  | 'zoom'
  | 'glow'
  | 'wobble'
  | 'swing'
  | 'elastic'
  | 'orbit';

/** Loader size options */
export type LoaderSize = 'small' | 'medium' | 'large' | 'xlarge';

/** Loader speed options */
export type LoaderSpeed = 'slow' | 'normal' | 'fast';

/** Title position relative to loader */
export type TitlePosition = 'top' | 'bottom' | 'left' | 'right';

/** Base path for branding assets */
const BRANDING_ASSET_PATH = '/branding';

/** Path to company icon logo */
const DEFAULT_LOGO_PATH = `${BRANDING_ASSET_PATH}/company/complexcare-icon.svg`;

/**
 * HxLoader - Animated Loader Component with Icon Logo
 *
 * A versatile loading indicator that displays the Complex Care Solutions icon logo with a variety of animation effects.
 * Supports customizable size, speed, title text, and multiple animation styles.
 *
 * @example
 *   ```html
 *   <!-- Basic loader with spin animation -->
 *   <hx-loader animation="spin" />
 *
 *   <!-- Loader with title -->
 *   <hx-loader animation="pulse" title="Loading data..." />
 *
 *   <!-- Custom size and speed -->
 *   <hx-loader animation="bounce" size="large" speed="fast" />
 *
 *   <!-- Title positioned on top -->
 *   <hx-loader animation="orbit" title="Please wait" titlePosition="top" />
 *   ```;
 */
@Component({
  selector: 'hx-loader',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './loader.html',
  styleUrls: ['./loader.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxLoader {
  /**
   * Animation effect to apply to the loader
   *
   * @default 'spin'
   */
  readonly animation = input<LoaderAnimation>('spin');

  /**
   * Size of the loader
   *
   * @default 'medium'
   */
  readonly size = input<LoaderSize>('medium');

  /**
   * Animation speed
   *
   * @default 'normal'
   */
  readonly speed = input<LoaderSpeed>('normal');

  /** Custom width (CSS length value) Overrides size prop when provided */
  readonly width = input<string | undefined>(undefined);

  /** Custom height (CSS length value) Overrides size prop when provided */
  readonly height = input<string | undefined>(undefined);

  /** Loading title text to display If not provided, no title is shown */
  readonly title = input<string | undefined>(undefined);

  /**
   * Position of the title relative to loader
   *
   * @default 'bottom'
   */
  readonly titlePosition = input<TitlePosition>('bottom');

  /** Custom CSS classes to apply to loader container */
  readonly cssClass = input<string>('');

  /** Custom CSS classes to apply to title text */
  readonly titleCssClass = input<string>('');

  /** Path to custom logo SVG Defaults to Complex Care icon logo */
  readonly logoPath = input<string>(DEFAULT_LOGO_PATH);

  /**
   * Alt text for the logo image
   *
   * @default 'Loading'
   */
  readonly alt = input<string>('Loading');

  /**
   * ARIA live region politeness level
   *
   * @default 'polite'
   */
  readonly ariaLive = input<'polite' | 'assertive' | 'off'>('polite');

  /** ARIA label for additional context If not provided, uses title or 'Loading' */
  readonly ariaLabel = input<string | undefined>(undefined);

  /**
   * Whether to show animated background overlay
   *
   * @default false
   */
  readonly overlay = input<boolean>(false);

  /**
   * Background color for overlay (when overlay=true)
   *
   * @default 'rgba(255, 255, 255, 0.9)'
   */
  readonly overlayColor = input<string>('rgba(255, 255, 255, 0.9)');

  /** Internal state: whether to show fallback display */
  protected readonly showFallback = signal(false);

  /** Computed: Container CSS classes */
  protected readonly containerClasses = computed(() => {
    const classes = ['hx-loader-container'];

    // Title position class
    if (this.title()) {
      classes.push(`hx-loader-container--${this.titlePosition()}`);
    }

    // Overlay class
    if (this.overlay()) {
      classes.push('hx-loader-container--overlay');
    }

    // Custom classes
    if (this.cssClass()) {
      classes.push(this.cssClass());
    }

    return classes.join(' ');
  });

  /** Computed: Loader CSS classes */
  protected readonly loaderClasses = computed(() => {
    const classes = ['hx-loader'];

    // Animation class
    classes.push(`hx-loader--${this.animation()}`);

    // Size class (if no custom dimensions)
    if (!this.width() && !this.height()) {
      classes.push(`hx-loader--${this.size()}`);
    }

    // Speed class
    classes.push(`hx-loader--${this.speed()}`);

    // Fallback class
    if (this.showFallback()) {
      classes.push('hx-loader--fallback');
    }

    return classes.join(' ');
  });

  /** Computed: Title CSS classes */
  protected readonly titleClasses = computed(() => {
    const classes = ['hx-loader__title'];

    // Custom title classes
    if (this.titleCssClass()) {
      classes.push(this.titleCssClass());
    }

    return classes.join(' ');
  });

  /** Computed: Width style */
  protected readonly loaderWidth = computed(() => {
    return this.width() || undefined;
  });

  /** Computed: Height style */
  protected readonly loaderHeight = computed(() => {
    return this.height() || undefined;
  });

  /** Computed: Overlay background style */
  protected readonly overlayStyle = computed(() => {
    return this.overlay() ? { backgroundColor: this.overlayColor() } : undefined;
  });

  /** Computed: Resolved ARIA label */
  protected readonly resolvedAriaLabel = computed(() => {
    return this.ariaLabel() || this.title() || 'Loading';
  });

  /** Handle image load errors */
  protected handleImageError(event: Event): void {
    const error = new Error(`Failed to load loader logo: ${this.logoPath()}`);
    console.error(error.message);
    this.showFallback.set(true);
  }
}
